import { useGraphQLQuery } from '@opengov/bnp-core';
import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { IPublication, IPublicationFolderWithStories, IStoryFolder } from '@opengov/stories-shared/types';
import {
  filterEmptySections,
  generateDataStructureForSections,
  sortListByTitleAndOrder
} from '@opengov/stories-shared/utils/publication';
import { buildPublicationFolderHierarchyMap } from '@opengov/stories-shared/utils/general';

import { getFragmentData, graphql } from '../../../../../gql-generated/hasura';
import {
  GetPublicationWithSectionsAndStoriesForCustomCssQuery,
  GetPublicationWithSectionsAndStoriesForCustomCssQueryVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import {
  publicationCustomCssQueryKeys,
  PublicationWithSectionsKeyParams
} from './publicationCustomCssQueryKeys';

const CustomCss_StoryFolderFieldsFragment = graphql(`
  fragment CustomCss_StoryFolderFields on storyFolders {
    id
    title
    order
    parentFolderId
    stories(where: { deletedAt: { _is_null: true } }, order_by: { order: asc }) {
      id: _id
      title
      order
    }
  }
`);

const GetPublicationWithSectionsAndStoriesForCustomCssDocument = graphql(`
  query GetPublicationWithSectionsAndStoriesForCustomCss($publicationId: uuid!, $entityId: Int!) {
    publicationRootFolder: storyFolders(
      where: {
        publicationId: { _eq: $publicationId }
        entityId: { _eq: $entityId }
        parentFolder: { parentFolderId: { _is_null: true } }
      }
    ) {
      ...CustomCss_StoryFolderFields
    }
    publication: publications_by_pk(id: $publicationId) {
      id
      status
      title
      description
      updatedAt
      customCssCode
      publicUrl
      publicationFolders: storyFolders(
        where: {
          publicationId: { _eq: $publicationId }
          entityId: { _eq: $entityId }
          deletedAt: { _is_null: true }
        }
      ) {
        ...CustomCss_StoryFolderFields
        subFolders(where: { deletedAt: { _is_null: true } }) {
          ...CustomCss_StoryFolderFields
        }
      }
    }
  }
`);

export type UsePublicationWithSectionsParams = PublicationWithSectionsKeyParams | null;

export type IPublicationWithSectionsResult = {
  publication: IPublication;
  storiesSelectData: IAccordionListNode[];
};

function transformToStoriesSelectData(sections: IStoryFolder[]): IAccordionListNode[] {
  const filteredSections = sortListByTitleAndOrder(
    filterEmptySections(sections)
  ) as IPublicationFolderWithStories[];
  // The "Showing story" dropdown should start collapsed — only expand a
  // section once the user searches into it (see FolderTreeSelect's filter).
  return generateDataStructureForSections(filteredSections, false);
}

export function usePublicationWithSectionsAndStories(
  params: UsePublicationWithSectionsParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useGraphQLQuery<
    GetPublicationWithSectionsAndStoriesForCustomCssQuery,
    GetPublicationWithSectionsAndStoriesForCustomCssQueryVariables,
    IPublicationWithSectionsResult | null
  >({
    queryKey: params
      ? publicationCustomCssQueryKeys.publicationWithSections(params)
      : ([...publicationCustomCssQueryKeys.all, 'publicationWithSections', 'disabled'] as const),
    document: GetPublicationWithSectionsAndStoriesForCustomCssDocument,
    url: HASURA_SERVICE_URL,
    variables: params
      ? { publicationId: params.publicationId, entityId: params.entityId }
      : { publicationId: '', entityId: 0 },
    options: {
      enabled: params !== null,
      onError: () => {
        toggleToast({ title: errorMessage, severity: 'error' });
      },
      select: (
        data: GetPublicationWithSectionsAndStoriesForCustomCssQuery
      ): IPublicationWithSectionsResult | null => {
        const publication = data.publication;
        if (!publication) return null;

        // A publication with no folder structure should still render its
        // title and an empty story dropdown — only the sections list
        // depends on `publicationRootFolder` being present.
        const rootFolderEntry = data.publicationRootFolder[0];
        let storiesSelectData: IAccordionListNode[] = [];
        if (rootFolderEntry && publication.publicationFolders) {
          // Codegen masks fragment fields behind `$fragmentRefs` markers;
          // `getFragmentData` unmasks so we can read the rootFolder's `id`.
          const rootFolderData = getFragmentData(CustomCss_StoryFolderFieldsFragment, rootFolderEntry);

          // Fragment-masked types carry `$fragmentRefs` placeholders instead of
          // concrete fields. The runtime shape matches IStoryFolder once Hasura
          // fills the fragment, so the cast bridges the type-level gap.
          const folderHierarchy = buildPublicationFolderHierarchyMap(
            publication.publicationFolders as unknown as IStoryFolder[]
          );
          const rootFolder = folderHierarchy[rootFolderData.id];
          const sections = rootFolder?.subFolders ?? [];
          storiesSelectData = transformToStoriesSelectData(sections as unknown as IStoryFolder[]);
        }

        return {
          publication: publication as unknown as IPublication,
          storiesSelectData
        };
      }
    }
  });
}
